import type { DiffActivity } from "../../../models";

export interface ColumnDef {
  key: string;
  label: string;
  format: "date" | "duration" | "text";
}

/**
 * Columns displayed in the diff activity table — exactly the fields that
 * mutation paths write to (status, actual_*, restart/reend, durations).
 */
export const MAIN_COLUMNS: ColumnDef[] = [
  { key: "status", label: "Status", format: "text" },
  { key: "actual_start", label: "Actual Start", format: "date" },
  { key: "actual_finish", label: "Actual Finish", format: "date" },
  { key: "restart_date", label: "Restart", format: "date" },
  { key: "reend_date", label: "Reend", format: "date" },
  { key: "expect_end_date", label: "Expected Finish", format: "date" },
  { key: "original_duration", label: "Orig Duration", format: "duration" },
  { key: "remaining_duration", label: "Rem Duration", format: "duration" },
];

const MAIN_COLUMN_KEYS = new Set(MAIN_COLUMNS.map((c) => c.key));

/**
 * Format a date string for display: "2026-01-30" -> "1/30".
 * Returns the raw value if it doesn't match the expected pattern.
 */
export function formatDate(val: string | null): string {
  if (!val) return "";
  // Expecting "YYYY-MM-DD" or "YYYY-MM-DD HH:MM"
  const match = val.match(/^\d{4}-(\d{2})-(\d{2})/);
  if (match) {
    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    return `${month}/${day}`;
  }
  return val;
}

/**
 * Format a duration value for display. Durations from P6 are in hours;
 * divide by 8 for workdays.
 */
export function formatDuration(val: string | null): string {
  if (!val) return "";
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  // DB stores durations in workdays already
  return num === Math.floor(num) ? String(num) : num.toFixed(1);
}

/**
 * Format a value based on its column type.
 */
export function formatValue(val: string | null, format: ColumnDef["format"]): string {
  if (format === "date") return formatDate(val);
  if (format === "duration") return formatDuration(val);
  return val || "";
}

/**
 * Return only the MAIN_COLUMNS that have at least one activity with that key
 * in its changes dict.
 */
export function getActiveColumns(activities: DiffActivity[]): ColumnDef[] {
  const activeKeys = new Set<string>();
  for (const act of activities) {
    for (const key of Object.keys(act.changes)) {
      if (MAIN_COLUMN_KEYS.has(key)) {
        activeKeys.add(key);
      }
    }
  }
  return MAIN_COLUMNS.filter((col) => activeKeys.has(col.key));
}

